// CalendarSection.js - With integrated BookingSummary
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Collapse,
  IconButton,
  Fade,
  Tooltip,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MonthCalendar from 'components/organisms/monthCalendar/view';
import CompressedCalendarView from 'components/organisms/monthCalendar/compressedCalendarView';
import BookingSummary from 'components/organisms/monthCalendar/bookingSummary';

// Styled components with reduced vertical spacing
const SectionHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary
}));

// Pre-Approved Venue Component - Show instead of calendar for PA venues
const PreApprovedVenueCard = ({ venue }) => {
  const handleVisitWebsite = () => {
    if (venue?.website) {
      window.open(venue.website, '_blank');
    }
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ px: 2, py: 3, mb: 1, borderRadius: 1, textAlign: 'center' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        This venue requires direct booking through their website
      </Alert>
      
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Ready to book?
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visit <strong>{venue?.name}</strong>'s website to complete your booking and discuss custom arrangements.
      </Typography>
      
      {venue?.website ? (
        <Button
          variant="contained"
          color="primary"
          endIcon={<OpenInNewIcon />}
          onClick={handleVisitWebsite}
          size="large"
        >
          Visit Website
        </Button>
      ) : (
        <Alert severity="warning">
          Website information not available. Please contact the venue directly.
        </Alert>
      )}
    </Paper>
  );
};

// View types
const CALENDAR_VIEWS = {
  SUMMARY: 'summary',
  COMPRESSED: 'compressed',
  FULL: 'full'
};

const CalendarSection = ({
  slug,
  workAreaSlug = null,
  resourceSlug = null,
  selectedDate,
  selectedPrice,
  spaces = 1,
  handleDateSelect,
  loadingData,
  bookingType,
  inPaper = true,
  doBooking = () => { },
  preparingBooking = false,
  totalPrice = 0,
  totalPriceVAT = 0,
  workingDays = [1, 2, 3, 4, 5],
  selectedResource = null,
  venue = null,
  selectedCommunity = null,
  onCommunityChange = () => {},
  communityBookingsByDate = {}
}) => {
  // Determine initial states based on props
  const initialState = useMemo(() => {
    // Set initial expanded state
    let shouldExpand = true;
    let initialView = CALENDAR_VIEWS.COMPRESSED;
    

    if (bookingType === 'MR') {
      if (!resourceSlug) {
        // No resource selected, collapse with summary view
        shouldExpand = false;
        initialView = CALENDAR_VIEWS.SUMMARY;
      } else if (resourceSlug && !selectedDate) {
        // Resource selected but no date, expand with compressed view
        shouldExpand = true;
        initialView = CALENDAR_VIEWS.COMPRESSED;
      } else if (resourceSlug && selectedDate) {
        // Both resource and date selected, collapse with summary view
        shouldExpand = false;
        initialView = CALENDAR_VIEWS.SUMMARY;
      }
    } else {
      // For Hot Desk bookings, always expanded with compressed view
      shouldExpand = true;
      initialView = CALENDAR_VIEWS.COMPRESSED;
    }

    return { shouldExpand, initialView };
  }, []); // Calculate only once on mount

  // State to track if calendar is expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(initialState.shouldExpand);

  // State to track which view is currently active
  const [activeView, setActiveView] = useState(initialState.initialView);

  // This effect runs only when relevant props change to update the expanded state and view
  useEffect(() => {
    // Determine the appropriate state based on current props
    let newExpanded = isExpanded;
    let newView = activeView;

    if (bookingType !== 'MR') {
      // Hot Desk logic
      newExpanded = true;
      newView = CALENDAR_VIEWS.COMPRESSED;
      if (workAreaSlug && selectedDate) {
        newExpanded = false;
        setIsExpanded(false);
        newView = CALENDAR_VIEWS.SUMMARY;
      }
    } else {
      // Meeting Room logic
      if (!resourceSlug) {
        newExpanded = false;
        newView = CALENDAR_VIEWS.SUMMARY;
      } else if (resourceSlug && !selectedDate) {
        newExpanded = true;
        newView = CALENDAR_VIEWS.COMPRESSED;
      } else if (resourceSlug && selectedDate) {
        newExpanded = false;
        newView = CALENDAR_VIEWS.SUMMARY;
      }
    }

    // Only update state if there's an actual change to prevent infinite loops
    if (newExpanded !== isExpanded) {
      setIsExpanded(newExpanded);
    }

    if (newView !== activeView) {
      setActiveView(newView);
    }
  }, [bookingType, resourceSlug, selectedDate]);

  // Effect to log or perform actions based on the current view
  useEffect(() => {

    // Add any additional logic here if needed when the view changes
  }, [activeView]);


  // Wrapper for date selection to collapse calendar after selection
  const handleDateSelection = (priceObject) => {
    // First call the parent's date selection handler
    handleDateSelect(priceObject);

    // For meeting rooms, collapse after date selection if resource is selected
    if (bookingType === 'MR' && resourceSlug) {
      setIsExpanded(false);
      setActiveView(CALENDAR_VIEWS.SUMMARY);
    }
    // For hot desk, always keep expanded with the current view
  };

  // Format date for display in collapsed view
  const formatSelectedDate = (date) => {
    if (!date) return 'Select a date';
    return date.format('dddd, MMMM D, YYYY');
  };

  // Handler for switching to full calendar view
  const handleShowFullCalendar = () => {
    setActiveView(CALENDAR_VIEWS.FULL);
  };

  // Handler for switching back to compressed view
  const handleBackToCompressedView = () => {
    setActiveView(CALENDAR_VIEWS.COMPRESSED);
  };

  // Handler for expanding the calendar from summary view
  const handleExpandCalendar = () => {
    setIsExpanded(true);
    setActiveView(CALENDAR_VIEWS.COMPRESSED);
  };

  // Handler for collapsing the calendar to summary view
  const handleCollapseCalendar = () => {
    setIsExpanded(false);
    setActiveView(CALENDAR_VIEWS.SUMMARY);
  };

  // Memoize the calendar view to prevent unnecessary re-renders
  const calendarView = useMemo(() => {
    switch (activeView) {
      case CALENDAR_VIEWS.COMPRESSED:
        return (
          <Fade in={activeView === CALENDAR_VIEWS.COMPRESSED}>
            <Box>
              <CompressedCalendarView
                slug={slug}
                onDateSelect={handleDateSelection}
                defaultDate={selectedDate}
                selectedPrice={selectedPrice}
                onShowFullCalendar={handleShowFullCalendar}
                priceDataLoading={loadingData}
                workingDays={workingDays}
                fixedSlotPrice={selectedResource?.hourly_rate}
                isMeetingRoom={bookingType === 'MR'}
                venue={venue}
                communityBookingsByDate={communityBookingsByDate}
              />
            </Box>
          </Fade>
        );

      case CALENDAR_VIEWS.FULL:
      default:
        return (
          <Fade in={activeView === CALENDAR_VIEWS.FULL}>
            <Box sx={{ mt: 1 }}>
              <MonthCalendar
                slug={slug}
                onDateSelect={handleDateSelection}
                defaultDate={selectedDate}
                selectedPrice={selectedPrice}
                spaces={spaces}
                preparingBooking={false}
                doBooking={doBooking}
                priceDataLoading={loadingData}
                availableBookingTypes={[bookingType]}
                initialBookingType={bookingType}
                //disablePastDates={true}
                showBookingSummary={false} // Disable internal booking summary
                fixedSlotPrice={selectedResource?.hourly_rate}
                isMeetingRoom={bookingType === 'MR'}
                communityBookingsByDate={communityBookingsByDate}
              />

              <Button
                variant="text"
                size="small"
                sx={{ mt: 1, display: 'block', margin: '0 auto' }}
                onClick={handleBackToCompressedView}
              >
                Back to Quick View
              </Button>
            </Box>
          </Fade>
        );
    }
  }, [
    activeView,
    slug,
    handleDateSelection,
    selectedDate,
    selectedPrice,
    handleShowFullCalendar,
    loadingData,
    workingDays,
    workAreaSlug,
    resourceSlug,
    spaces,
    doBooking,
    bookingType,
    handleBackToCompressedView,
    communityBookingsByDate
  ]);

  // If not wrapped in Paper, just return the calendar view
  if (!inPaper) {
    return (
      <>
        {calendarView}
        <BookingSummary
          selectedPrice={selectedPrice}
          selectedDate={selectedDate}
          spaces={spaces}
          totalPrice={totalPrice}
          totalPriceVAT={totalPriceVAT}
          preparingBooking={preparingBooking}
          priceDataLoading={loadingData}
          doBooking={doBooking}
          bookingType={bookingType}
          selectedCommunity={selectedCommunity}
          onCommunityChange={onCommunityChange}
        />
      </>
    );
  }

  // Check if venue is Pre-Approved - show simplified booking flow
  if (venue?.status === 'PA') {
    return <PreApprovedVenueCard venue={venue} />;
  }

  // Main render with Paper wrapper
  return (
    <Paper elevation={0} variant="outlined" sx={{ px: 1.5, py: 0, mb: 1, borderRadius: 1 }}>
      {/* Summary view - shown when collapsed for meeting rooms */}
      {!isExpanded && activeView === CALENDAR_VIEWS.SUMMARY && (
        <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <CalendarTodayIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.2rem' }} />
            <Typography variant="subtitle2" fontWeight="medium">
              {selectedDate ? formatSelectedDate(selectedDate) : 'No date selected'}
            </Typography>
          </Box>
          <Tooltip
            title={bookingType === 'MR' && !resourceSlug ? "Please select a meeting room first" : ""}
            placement="top"
            arrow
            disableHoverListener={!(bookingType === 'MR' && !resourceSlug)} // Only show tooltip when button is disabled
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                sx={{ minWidth: '60px', py: 0.5 }}
                onClick={handleExpandCalendar}
                disabled={bookingType === 'MR' && !resourceSlug}
              >
                {selectedDate ? 'Change' : 'Select'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Expanded calendar view */}
      <Collapse in={isExpanded}>
        {/* Show collapse button only for meeting rooms with resource and date selected */}
        {selectedDate && isExpanded && bookingType === 'MR' && resourceSlug && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, mb: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ pl: 1 }}>
              Select your date
            </Typography>
            <IconButton
              onClick={handleCollapseCalendar}
              size="small"
              color="primary"
              sx={{ p: 0.5 }}
            >
              <ExpandLessIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Render the appropriate calendar view */}
        {calendarView}
      </Collapse>

      {/* Booking Summary - Rendered after calendar view */}
      {selectedPrice && selectedDate && <BookingSummary
        selectedPrice={selectedPrice}
        selectedDate={selectedDate}
        spaces={spaces}
        totalPrice={totalPrice}
        totalPriceVAT={totalPriceVAT}
        preparingBooking={preparingBooking}
        priceDataLoading={loadingData}
        doBooking={doBooking}
        bookingType={bookingType}
        selectedCommunity={selectedCommunity}
        onCommunityChange={onCommunityChange}
      />}
    </Paper>
  );
};

export default CalendarSection;